import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { getAdaptationsDone, getAnalysisComplete, getAssetsDone } from "@/tools/aggregate/portfolio-export/utils/progress";
import { useEffect, useRef, useState } from "react";
import styles from "./PortfolioExportJobInformation.module.scss";
const UPLOAD_INTERVAL_MS = 2500; // when status=uploading (no progress tracking), duration each progress message displays during uploading status
const MIN_DWELL_MS = 2000; // when status=processing, (despite having progress tracking) min duration each progress message displays

const UPLOAD_MESSAGES = ["Processing and validating coordinates", "Accessing elevation data"];

// portfolio processing progress messages
const ASSET_MESSAGES = ["Applying hazard layers", "Running climate models", "Applying asset-specific settings", "Calculating asset exposure and risk"];
// largeSite processing progress messages
const POINT_MESSAGES = ["Applying hazard layers", "Running climate models", "Calculating point exposure and risk"];
// adaptations processing progress messages
const ADAPTATION_MESSAGES = ["Applying different resilience settings", "Running automated resilience pathways", "Calculating risk envelope"];
const COMPLETE_MESSAGES = ["Finalising point-level analysis"];
type ProgressPhase = "uploading" | "assets" | "adaptations" | "complete" | null;
const getProgressPhase = (schema: PortfolioExportJobSchema, canRunAutoAdaptation: boolean): ProgressPhase => {
  const adaptations = schema.adaptations;
  const adaptationsInProgress = canRunAutoAdaptation && !!adaptations && adaptations.totalCount > 0 && adaptations.processedCount + adaptations.errorCount < adaptations.totalCount;

  // adaptation progresses (in ui) after asset processing is complete, so check this first
  if (adaptationsInProgress) return "adaptations";
  if (getAssetsDone(schema) && getAdaptationsDone(schema, canRunAutoAdaptation) && !getAnalysisComplete(schema, canRunAutoAdaptation)) {
    return "complete";
  }
  if (schema.status === "processing") return "assets";
  if (schema.status === "uploading") return "uploading";
  return null;
};
const getMessages = (phase: ProgressPhase, isLargeSite: boolean): string[] => {
  switch (phase) {
    case "uploading":
      return UPLOAD_MESSAGES;
    case "assets":
      return isLargeSite ? POINT_MESSAGES : ASSET_MESSAGES;
    case "adaptations":
      return ADAPTATION_MESSAGES;
    case "complete":
      return COMPLETE_MESSAGES;
    default:
      return [];
  }
};

/** Real progress count that drives message advancement; undefined for the timer-based/static phases. */
const getProgressPhaseCount = (phase: ProgressPhase, schema: PortfolioExportJobSchema): number | undefined => {
  if (phase === "assets") return schema.assets.processedCount;
  if (phase === "adaptations") return schema.adaptations?.processedCount;
  return undefined;
};
type PortfolioExportProgressMessagesProps = {
  schema: PortfolioExportJobSchema;
  canRunAutoAdaptation: boolean;
};
export const PortfolioExportProgressMessages = ({
  schema,
  canRunAutoAdaptation
}: PortfolioExportProgressMessagesProps) => {
  const progressPhase = getProgressPhase(schema, canRunAutoAdaptation);
  const messages = getMessages(progressPhase, schema.isLargeSite);
  const count = getProgressPhaseCount(progressPhase, schema);
  const [index, setIndex] = useState(0);
  const lastAdvanceRef = useRef(Date.now());
  const prevCountRef = useRef(count ?? 0);

  // Reset to the first message whenever the phase changes.
  useEffect(() => {
    setIndex(0);
    lastAdvanceRef.current = Date.now();
    prevCountRef.current = count ?? 0;
  }, [progressPhase]);
  useEffect(() => {
    if (progressPhase !== "uploading") return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % UPLOAD_MESSAGES.length);
    }, UPLOAD_INTERVAL_MS);
    return () => clearInterval(id);
  }, [progressPhase]);
  useEffect(() => {
    if (progressPhase !== "assets" && progressPhase !== "adaptations") return;
    if (count === undefined) return;

    // only run when progress count changes
    if (count <= prevCountRef.current) {
      prevCountRef.current = count;
      return;
    }
    prevCountRef.current = count;
    if (Date.now() - lastAdvanceRef.current < MIN_DWELL_MS) return;
    lastAdvanceRef.current = Date.now();
    setIndex(i => (i + 1) % messages.length);
  }, [count, progressPhase]);
  if (!messages.length) return null;
  return <span data-sentry-component="PortfolioExportProgressMessages" data-sentry-source-file="PortfolioExportProgressMessages.tsx">
      <span className={styles.progressMessage}>{messages[index % messages.length]}</span>
    </span>;
};